import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Button, Switch } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { Bell } from 'lucide-react';
import { formatDateTime } from '@/lib/format';
import { useServiceAlerts, useServiceAlertHistory, type ServiceAlert } from '@/api/services';
import { useUpsertServiceAlerts, type AlertThresholdPayload } from '@/api/services-mutations';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { NumericInput } from '@/components/numeric-input';

type Draft = { warning_threshold: string; critical_threshold: string; enabled: boolean };

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

const str = (v: unknown) => (v == null || v === '' ? '—' : String(v));

/** Seed local edit drafts keyed by config-option id from the fetched alerts. */
function seedDrafts(alerts: ServiceAlert[]): Record<number, Draft> {
  const next: Record<number, Draft> = {};
  for (const a of alerts) {
    next[a.service_configoption_id] = {
      warning_threshold: String(a.warning_threshold ?? 0),
      critical_threshold: String(a.critical_threshold ?? 0),
      enabled: a.enabled,
    };
  }
  return next;
}

/**
 * Alerts tab — per-config-option usage thresholds (warning / critical, toggle),
 * saved in bulk, plus the fired-alert history log. Thresholds are edited inline
 * from local draft state seeded off the fetched alerts.
 */
export function ServiceAlertsTab({ id }: { id: number }) {
  const toast = useToast();
  const alerts = useServiceAlerts(id);
  const history = useServiceAlertHistory(id);
  const upsert = useUpsertServiceAlerts(id);

  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const rows = alerts.data?.results ?? [];

  // Re-seed whenever the fetched alerts change (initial load / post-save refetch).
  useEffect(() => {
    if (alerts.data) setDrafts(seedDrafts(alerts.data.results ?? []));
  }, [alerts.data]);

  if (alerts.isError) return <TabError title="Failed to load alerts" onRetry={() => alerts.refetch()} />;
  if (alerts.isLoading) return <TabLoading rows={4} />;

  const patch = (cid: number, next: Partial<Draft>) =>
    setDrafts((s) => ({ ...s, [cid]: { ...s[cid], ...next } }));

  const save = () => {
    const payload: AlertThresholdPayload[] = rows.map((a) => {
      const d = drafts[a.service_configoption_id];
      return {
        service_configoption_id: a.service_configoption_id,
        name: a.name,
        threshold_type: a.threshold_type,
        warning_threshold: Number(d?.warning_threshold) || 0,
        critical_threshold: Number(d?.critical_threshold) || 0,
        enabled: d?.enabled ?? a.enabled,
      };
    });
    upsert.mutate(payload, {
      onSuccess: () => toast.success('Thresholds saved'),
      onError: (e) => toast.error(errMsg(e)),
    });
  };

  const columns: ColumnDef<ServiceAlert, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name}</span> },
    { id: 'unit', header: 'Unit', cell: ({ row }) => str(row.original.unit) },
    {
      id: 'warning_threshold',
      header: 'Warning threshold',
      cell: ({ row }) => {
        const cid = row.original.service_configoption_id;
        return (
          <NumericInput
            value={drafts[cid]?.warning_threshold ?? ''}
            onChange={(e) => patch(cid, { warning_threshold: e.target.value })}
          />
        );
      },
    },
    {
      id: 'critical_threshold',
      header: 'Critical threshold',
      cell: ({ row }) => {
        const cid = row.original.service_configoption_id;
        return (
          <NumericInput
            value={drafts[cid]?.critical_threshold ?? ''}
            onChange={(e) => patch(cid, { critical_threshold: e.target.value })}
          />
        );
      },
    },
    {
      id: 'enabled',
      header: 'Enabled',
      cell: ({ row }) => {
        const cid = row.original.service_configoption_id;
        return (
          <Switch
            checked={drafts[cid]?.enabled ?? false}
            onCheckedChange={(checked) => patch(cid, { enabled: checked })}
            aria-label={`Toggle alert for ${row.original.name}`}
          />
        );
      },
    },
  ];

  const historyRows = history.data?.results ?? [];
  const historyColumns: ColumnDef<Record<string, unknown>, unknown>[] = [
    { id: 'created_at', header: 'When', cell: ({ row }) => formatDateTime(row.original.created_at as string | undefined) },
    { id: 'name', header: 'Alert', cell: ({ row }) => str(row.original.description ?? row.original.name) },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;
        if (status == null || status === '') return '—';
        const s = String(status).toLowerCase();
        const tone = s.includes('critical') || s.includes('danger') ? 'danger' : s.includes('warning') ? 'warning' : s.includes('resolved') || s.includes('ok') ? 'success' : 'neutral';
        return <StatusPill tone={tone}>{String(status)}</StatusPill>;
      },
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card flat>
        <CardHeader
          actions={
            <Button size="sm" variant="primary" disabled={upsert.isPending || rows.length === 0} onClick={save}>
              Save thresholds
            </Button>
          }
        >
          <CardTitle>Usage alerts</CardTitle>
        </CardHeader>
        <CardBody>
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => String(r.service_configoption_id)}
            emptyState={<EmptyState icon={<Bell size={24} />} title="No alerts configured" description="This service has no usage alerts." />}
          />
        </CardBody>
      </Card>

      <Card flat>
        <CardHeader>
          <CardTitle>Alert history</CardTitle>
        </CardHeader>
        <CardBody>
          <DataTable
            columns={historyColumns}
            data={historyRows}
            emptyState={<EmptyState icon={<Bell size={24} />} title="No alert history" description="No alerts have fired for this service." />}
          />
        </CardBody>
      </Card>
    </div>
  );
}
